/**
 * Which product field a mapping actually reads and writes for THIS product.
 *
 * A metadata source may list `altKeys` — sibling fields holding the same figure
 * for another brand's line (see `FormFieldSource`). The first key with a value
 * wins, `key` first, and a mapping with nothing populated keeps `key` so a form
 * filled from scratch still writes somewhere sensible.
 *
 * `brandKeys` is checked FIRST and does not look at values at all: where a
 * brand keeps that figure in its own field, that field is the answer even when
 * it is empty and the default key is not. `usMapPrice` on a DJ product holds
 * the last published price, so a value-based rule picks it and prints a stale
 * figure (#768).
 */
export function resolveMetadataKey(source, metadata) {
    const brand = typeof metadata.brand === 'string' ? metadata.brand.trim().toLowerCase() : '';
    if (brand && source.brandKeys) {
        const match = Object.entries(source.brandKeys).find(([b]) => b.toLowerCase() === brand);
        if (match)
            return match[1];
    }
    for (const key of [source.key, ...(source.altKeys ?? [])]) {
        const v = metadata[key];
        if (v !== undefined && v !== null && v !== '')
            return key;
    }
    return source.key;
}
/**
 * A copy of `template` with every metadata key resolved against this product.
 *
 * Resolving ONCE, at the edge, is what keeps read and write in step: seeding,
 * the key captioned in the editor, and the write-back all consume the same
 * concrete keys, so none of them can disagree about which field a line means.
 * `altKeys` and `brandKeys` are dropped from the result — they have already
 * been spent.
 */
export function resolveTemplateKeys(template, metadata) {
    return {
        ...template,
        fields: template.fields.map((field) => field.source.kind === 'metadata' && (field.source.altKeys || field.source.brandKeys)
            ? { ...field, source: { kind: 'metadata', key: resolveMetadataKey(field.source, metadata) } }
            : field),
    };
}
const CURRENCY_SYMBOLS = {
    USD: '$',
    EUR: '€',
    GBP: '£',
};
/** All three symbols are WinAnsi-encodable, so they survive `fillTemplate`. */
export function currencySymbol(currency = 'USD') {
    return CURRENCY_SYMBOLS[currency] ?? CURRENCY_SYMBOLS.USD;
}
/**
 * Format a metadata value as currency for a form field: symbol, thousands
 * separators, always two decimals.
 *
 * Non-numeric text passes through VERBATIM. That is not defensive padding —
 * `usMapPrice` is genuinely text-typed and legitimately holds "OPEN" or "---",
 * and a price list that reads "$NaN" is worse than one that reads "OPEN".
 */
export function formatFormCurrency(value, currency = 'USD') {
    if (value === null || value === undefined || value === '')
        return '';
    const num = typeof value === 'number' ? value : Number(String(value).replace(/[$,€£\s]/g, ''));
    if (!Number.isFinite(num))
        return String(value);
    return `${currencySymbol(currency)}${num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}
/**
 * The amount half of `formatFormCurrency`: two decimals, no symbol and no
 * thousands separators, so the result is still a valid `<input type="number">`
 * value.
 *
 * The product form prints the symbol as an adornment and binds the stored
 * number straight to a number input, which is why a currency FORMULA field
 * (`costInEuro` = `{EUR_RATE} * [Actual Cost]`) rendered there as
 * `€ 227.27272727272725` while the product table and the spreadsheet — both of
 * which go through `formatFormCurrency` — showed `€227.27` (#817). The stored
 * value keeps its full precision; only the rendering rounds.
 *
 * Non-numeric text passes through VERBATIM, same rule as `formatFormCurrency`.
 */
export function formatCurrencyAmount(value) {
    if (value === null || value === undefined || value === '')
        return '';
    const num = parseFormCurrency(value);
    return num === null ? String(value) : num.toFixed(2);
}
/**
 * Read a currency string back to a number. Returns null for anything that is
 * not a number — blank, or the "OPEN"/"---" text above — so a caller can tell
 * "no numeric value" from zero.
 */
export function parseFormCurrency(input) {
    if (input === null || input === undefined || input === '')
        return null;
    if (typeof input === 'number')
        return Number.isFinite(input) ? input : null;
    const stripped = String(input).replace(/[$,€£\s]/g, '');
    if (stripped === '')
        return null;
    const num = Number(stripped);
    return Number.isFinite(num) ? num : null;
}
/**
 * The cost multiplier: US List Price ÷ Actual Cost.
 *
 * This is what the product record calls `margin` — the Smartsheet column was
 * named that way and the name stuck, but the formula has always been a
 * multiplier, not a margin percentage (issue #250, and now #419).
 *
 * It is recomputed here rather than read from `metadata.margin` so the form
 * shows a value derived from the two costs printed alongside it. A form whose
 * Multiplier disagrees with its own List and Cost lines is worse than a blank.
 *
 * `actualCost` is the key production data actually populates; `usActualCost`
 * (added by the 2026-05-29 rename migration, which also kept the original) is
 * empty on the catalogue, so it is only a fallback.
 */
export function computeMultiplier(metadata) {
    const list = parseFormCurrency(metadata.usListPrice);
    const cost = parseFormCurrency(metadata.actualCost) ?? parseFormCurrency(metadata.usActualCost);
    if (list === null || cost === null || cost === 0)
        return null;
    return list / cost;
}
/** The multiplier as a form string, e.g. "4.85". Blank when it cannot be derived. */
export function formatMultiplier(metadata) {
    const m = computeMultiplier(metadata);
    return m === null ? '' : m.toFixed(2);
}
/**
 * Seed a form's values from a product.
 *
 * Pure and total: every branch produces a string, and a field with nothing to
 * seed from is simply absent from the result rather than present-and-empty.
 * That absence is load-bearing: the form service spreads this output OVER a
 * form's stored values on every read (#808), so a key emitted here wins and a
 * key omitted falls through to what the document holds. Emitting "" for a field
 * the product has nothing for would blank it instead of deferring.
 *
 * `today` is injected rather than read from the clock so the output is testable.
 */
export function seedFormValues(template, metadata, opts = {}) {
    const today = opts.today ?? new Date().toISOString().slice(0, 10);
    const values = {};
    for (const field of template.fields) {
        if (field.source.kind === 'barcode') {
            // The field is read-only, so a stored value is only ever an old copy of the
            // registry's. Once the registry has been asked, blank beats that copy (#1340).
            if (!opts.barcode)
                continue;
            const v = opts.barcode[field.source.key];
            values[field.pdfField] = v === undefined || v === null ? '' : String(v);
            continue;
        }
        if (field.source.kind === 'metadata') {
            const v = metadata[field.source.key];
            if (v === undefined || v === null || v === '')
                continue;
            values[field.pdfField] =
                field.format === 'currency' ? formatFormCurrency(v, field.currency) : formValueString(v);
            continue;
        }
        if (field.source.kind === 'computed') {
            switch (field.source.id) {
                case 'todaysDate':
                    values[field.pdfField] = today;
                    break;
                case 'multiplier': {
                    const m = formatMultiplier(metadata);
                    if (m !== '')
                        values[field.pdfField] = m;
                    break;
                }
            }
            continue;
        }
        // `manual` fields start empty — nothing in ATLAS to seed them from.
    }
    return values;
}
/**
 * A multiselect metadata value as a `string[]`, whatever shape it was stored in.
 *
 * Certifications held comma-strings for years (#839), and product versions older
 * than the migration keep that shape, so every reader accepts both: an array
 * passes through, a legacy string splits on commas, semicolons and newlines.
 * Blank entries drop and duplicates collapse.
 */
export function toMultiValue(value) {
    const parts = Array.isArray(value)
        ? value.map((v) => String(v))
        : typeof value === 'string'
            ? value.split(/[,;\n]/)
            : [];
    return [...new Set(parts.map((p) => p.trim()).filter(Boolean))];
}
/**
 * A metadata value as a form or display string. Arrays join with ", ", the
 * separator the form's dropdown picker writes, so an untouched multiselect
 * compares equal on save instead of reading as an edit.
 */
export function formValueString(value) {
    if (value === undefined || value === null)
        return '';
    return Array.isArray(value) ? toMultiValue(value).join(', ') : String(value);
}
/**
 * Coerce an edited form value to the type the product record expects, given the
 * metadata field's declared type.
 *
 * Currency and number fields are validated as `z.number()` (see
 * `zodForField`), so writing the display string back would either be rejected
 * or — because the form service calls `productService.update` below the route's
 * validation — silently store a string where every reader expects a number.
 *
 * Returns `undefined` when the value cannot be represented, which callers treat
 * as "do not write this field" rather than "write null".
 */
export function coerceMetadataValue(value, fieldType, format) {
    if (typeof value === 'boolean')
        return value;
    switch (fieldType) {
        case 'multiselect':
            // The form's picker joins its chips with ", " (see `formValueString`).
            return toMultiValue(value);
        case 'currency':
        case 'number':
        case 'integer': {
            if (value.trim() === '')
                return null;
            const num = parseFormCurrency(value);
            // Non-numeric text in a numeric field (a `usMapPrice` reading "OPEN") is
            // left alone rather than written as NaN or as a string.
            return num === null ? undefined : num;
        }
        case 'boolean':
            if (value === '')
                return null;
            return value === 'true' || value === 'Yes';
        default:
            // A money line on a form is not always a numeric field. `usMapPrice` and
            // `djMold` are typed `text` because they legitimately hold "OPEN",
            // "SHARED" and "6800 (shared 1/3)" — the currency is a DISPLAY hint only.
            // Every stored value is nonetheless a bare string ("7250"), so strip the
            // symbol and separators the form added rather than storing "$7,250.00"
            // and leaving the spreadsheet to render a doubled-up "$$7,250.00".
            // Non-numeric text falls through untouched, which is the whole reason
            // these fields are text.
            if (format === 'currency') {
                const num = parseFormCurrency(value);
                if (num !== null)
                    return String(num);
            }
            return value;
    }
}
/**
 * The name an exported form is filed and archived under.
 *
 * Version-stamped because the PDF is the record. A form's stored `values` are
 * about to stop being the evidence of what was sent — basic fields resolve live
 * from the product (#808) — so the archived document is all that remains, and
 * two exports of the same form on the same day must not collide on a name or,
 * more importantly, on a storage key.
 *
 * The sanitiser is deliberately an allow-list: a product name carries spaces,
 * slashes and quote marks, none of which belong in a file name or an object key.
 */
export function exportFileName(templateId, productName, version, date = new Date()) {
    const safeName = String(productName).replace(/[^\w.-]+/g, '_');
    return `${templateId.toUpperCase()}_${safeName}_${date.toISOString().slice(0, 10)}_v${version}.pdf`;
}
//# sourceMappingURL=product-form-values.js.map